import React, { useState, useEffect, useRef, useMemo, useLayoutEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import backend from "../../backend";

/**
 * WorkOrderLookup — busca OTs activas por número, nombre o cliente.
 *
 * Props:
 *   value       { workOrderId, number, name } | null
 *   onChange    (value | null) => void
 *   placeholder string
 *   disabled    boolean
 */
const WorkOrderLookup = ({
  value,
  onChange,
  placeholder = "OT que origina (opcional)...",
  disabled = false,
}) => {
  const [query,        setQuery]        = useState(value ? `OT-${String(value.number).padStart(4,"0")}` : "");
  const [workOrders,   setWorkOrders]   = useState([]);
  const [open,         setOpen]         = useState(false);
  const [dropdownRect, setDropdownRect] = useState(null);
  const [loaded,       setLoaded]       = useState(false);

  const inputRef     = useRef(null);
  const dropdownRef  = useRef(null);
  const containerRef = useRef(null);

  const loadOnce = useCallback(async () => {
    if (loaded) return;
    try {
      const res = await backend.get("workOrder?status=pending,working,approved&sort=-number&limit=200");
      setWorkOrders(res.data || []);
      setLoaded(true);
    } catch {}
  }, [loaded]);

  useEffect(() => {
    setQuery(value ? `OT-${String(value.number).padStart(4, "0")} — ${value.name || ""}`.trim() : "");
  }, [value]);

  useLayoutEffect(() => {
    if (open && inputRef.current) {
      const r = inputRef.current.getBoundingClientRect();
      setDropdownRect({ top: r.bottom + 2, left: r.left, width: r.width });
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      if (inputRef.current) {
        const r = inputRef.current.getBoundingClientRect();
        setDropdownRect({ top: r.bottom + 2, left: r.left, width: r.width });
      }
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  useEffect(() => {
    const handler = e => {
      if (!containerRef.current?.contains(e.target) && !dropdownRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return workOrders.slice(0, 8);
    const q = query.toLowerCase().replace(/^ot-?/i, "");
    return workOrders.filter(wo =>
      String(wo.number || "").includes(q) ||
      (wo.name  || "").toLowerCase().includes(q) ||
      (wo.client?.name || "").toLowerCase().includes(q)
    ).slice(0, 8);
  }, [workOrders, query]);

  const handleSelect = useCallback(wo => {
    onChange?.({ workOrderId: wo._id, number: wo.number, name: wo.name || "" });
    setOpen(false);
  }, [onChange]);

  const handleClear = useCallback(() => {
    onChange?.(null);
    setQuery("");
  }, [onChange]);

  const dropdown = open && dropdownRect
    ? ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: "fixed",
            top: dropdownRect.top,
            left: dropdownRect.left,
            width: Math.max(dropdownRect.width, 260),
            background: "#1a1a1a",
            border: "1px solid #2e2e2e",
            borderRadius: 6,
            maxHeight: 240,
            overflowY: "auto",
            zIndex: 10000,
            boxShadow: "0 4px 16px rgba(0,0,0,0.6)",
          }}
        >
          {filtered.length === 0 && (
            <div style={{ padding: "10px", color: "#aaa", fontSize: 11, textAlign: "center" }}>
              Sin OTs activas
            </div>
          )}
          {filtered.map(wo => (
            <div
              key={wo._id}
              onMouseDown={e => { e.preventDefault(); handleSelect(wo); }}
              style={{
                padding: "7px 10px", cursor: "pointer",
                borderBottom: "1px solid #1f1f1f",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#242424"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              <div>
                <div style={{ fontSize: 11, fontFamily: "monospace", color: "#f39c12" }}>
                  OT-{String(wo.number).padStart(4, "0")}
                </div>
                <div style={{ fontSize: 11, color: "#bbb", marginTop: 1 }}>{wo.name || "—"}</div>
              </div>
              {wo.client?.name && (
                <div style={{ fontSize: 10, color: "#999", flexShrink: 0, marginLeft: 8 }}>
                  {wo.client.name}
                </div>
              )}
            </div>
          ))}
        </div>,
        document.body
      )
    : null;

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <input
        ref={inputRef}
        className="bini-input"
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); onChange?.(null); }}
        onFocus={() => { loadOnce(); setOpen(true); }}
        onKeyDown={e => e.key === "Escape" && setOpen(false)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        style={{ paddingRight: value?.workOrderId ? 28 : undefined, fontSize: 11 }}
      />
      {value?.workOrderId && !disabled && (
        <button
          type="button"
          onClick={handleClear}
          style={{
            position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
            background: "none", border: "none", color: "#aaa", cursor: "pointer", fontSize: 14, lineHeight: 1,
          }}
        >×</button>
      )}
      {dropdown}
    </div>
  );
};

export default WorkOrderLookup;
